import { useCallback } from 'react';
import { odata } from '@bingads-webui/http-util';
import { useOdataOptions } from './use-odata-options';

// BulkFileType enum value for AIGC document uploads (.pdf, .docx)
const BULK_FILE_TYPE_AIGC_DOCUMENT = 22;

export interface UploadedFileInfo {
  BlobName: string;
  FileName: string;
  FileExtension: string;
  Status: string;
}

/**
 * Hook that checks the status of previously uploaded documents.
 * Used for resume scenarios — filters out files with status 'NotFound' (expired).
 *
 * Usage:
 *   const getUploadedFileInfoBatch = useGetUploadedFileInfoBatch();
 *   const validFiles = await getUploadedFileInfoBatch(blobNames);
 *
 * @returns {Function} accepts an array of blob names, returns valid file info
 */
export const useGetUploadedFileInfoBatch = (): (
  // eslint-disable-next-line no-unused-vars
  blobNames: string[]
) => Promise<UploadedFileInfo[]> => {
  const [odataOptions, { aid }] = useOdataOptions();

  return useCallback((blobNames: string[]) => {
    // Defensive guard: callers must pass an array. A bare string would be
    // silently serialized as character-array indices by OData, producing a
    // malformed body. Resolve to empty rather than firing a bad request.
    if (!Array.isArray(blobNames) || blobNames.length === 0) {
      return Promise.resolve([]);
    }

    const url = `/BulkUploadFiles/GetUploadedFileInfoBatch(AccountId=${aid})`;

    return odata.post({
      ...odataOptions,
      url,
      data: {
        BlobNames: blobNames,
        FileType: BULK_FILE_TYPE_AIGC_DOCUMENT,
      },
    })
      .then((response: UploadedFileInfo[] | { value?: UploadedFileInfo[] }) => {
        // The real API returns a bare array; some OData wrappers surface it
        // as `{ value: [...] }`. Accept either shape defensively.
        const items = Array.isArray(response)
          ? response
          : ((response && response.value) || []);

        // Filter out expired files (status = 'NotFound')
        return items.filter((item: UploadedFileInfo) => item.Status !== 'NotFound');
      });
  }, [odataOptions, aid]);
};
